import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Clock, Loader2, Trash2, X, Play } from 'lucide-react';
import Navigation from '../components/Navigation';
import VideoPlayer from '../components/VideoPlayer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HistoryPage = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedChannel, setSelectedChannel] = useState(null);

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/history`, {
        withCredentials: true
      });
      setHistory(response.data.history);
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  const handleClearHistory = async () => {
    if (window.confirm('Are you sure you want to clear your watch history?')) {
      try {
        await axios.delete(`${API_URL}/api/history`, {
          withCredentials: true
        });
        setHistory([]);
      } catch (error) {
        console.error('Error clearing history:', error);
      }
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds < 60) return 'Less than a minute';
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    }
    return `${minutes} min`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (minutes < 60) return `${minutes} min ago`;
    if (hours < 24) return `${hours} hours ago`;
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    return date.toLocaleDateString();
  };

  const defaultLogo = (name) => 
    `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=E50914&color=fff&size=200`;

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navigation />
      
      <main className="pt-20 pb-12">
        {/* Video Player Modal */}
        {selectedChannel && (
          <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4" data-testid="player-modal">
            <div className="w-full max-w-6xl">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold font-['Outfit']">{selectedChannel.name}</h2>
                <button
                  onClick={() => setSelectedChannel(null)}
                  className="p-2 text-[#A3A3A3] hover:text-white hover:bg-[#1A1A1A] rounded-lg transition-colors"
                  data-testid="close-player-btn"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <VideoPlayer url={selectedChannel.url} channelName={selectedChannel.name} />
            </div>
          </div>
        )}

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-[#E50914]" />
              <h1 className="text-3xl font-bold font-['Outfit']">Watch History</h1>
            </div>
            {history.length > 0 && (
              <button
                onClick={handleClearHistory}
                className="flex items-center gap-2 btn-secondary px-4 py-2 text-[#E50914] hover:bg-[#E50914]/10"
                data-testid="clear-history-btn"
              >
                <Trash2 className="w-4 h-4" />
                Clear History
              </button>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-10 h-10 text-[#E50914] animate-spin" />
            </div>
          ) : history.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <Clock className="w-16 h-16 text-[#262626] mb-4" strokeWidth={1} />
              <h3 className="text-xl font-semibold mb-2 font-['Outfit']">No Watch History</h3>
              <p className="text-[#A3A3A3] max-w-md">
                Channels you watch will appear here so you can easily find them again.
              </p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="history-list">
              {history.map((item, index) => (
                <div
                  key={`${item.id}-${index}`}
                  className="group flex items-center gap-4 p-4 bg-[#121212] border border-[#262626] rounded-lg hover:border-[#E50914]/30 transition-all cursor-pointer"
                  onClick={() => setSelectedChannel(item)}
                  data-testid={`history-item-${item.id}`}
                >
                  {/* Thumbnail */}
                  <div className="relative w-32 sm:w-40 aspect-video rounded-lg overflow-hidden bg-[#1A1A1A] flex-shrink-0">
                    <img
                      src={item.logo || defaultLogo(item.name)}
                      alt={item.name}
                      className="w-full h-full object-cover"
                      onError={(e) => e.target.src = defaultLogo(item.name)}
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <div className="w-10 h-10 bg-[#E50914] rounded-full flex items-center justify-center">
                        <Play className="w-5 h-5 text-white ml-0.5" fill="currentColor" />
                      </div>
                    </div>
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-semibold truncate group-hover:text-[#E50914] transition-colors">
                      {item.name}
                    </h3>
                    <p className="text-[#A3A3A3] text-sm mt-1">{item.category}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-[#A3A3A3]">
                      <span>{formatDate(item.watched_at)}</span>
                      <span>Watched {formatDuration(item.duration)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default HistoryPage;
